import React from "react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const services = [
  {
    title: "Application Security",
    desc: "Prevent vulnerabilities like XSS, broken authentication, insecure APIs, and SQL injection before they impact operations.",
    extra: "Secure coding practices",
    icon: "🛡️",
  },
  {
    title: "Data Protection",
    desc: "Protect sensitive customer, shipment, and operational data with strong encryption, policies, and access controls.",
    extra: "Encryption & access control",
    icon: "🔐",
  },
  {
    title: "Threat Monitoring",
    desc: "Detect suspicious behavior, unauthorized access, and abnormal events in real time across systems and users.",
    extra: "Alerts & logging",
    icon: "📡",
  },
  {
    title: "System Hardening",
    desc: "Reduce risk exposure by securing configurations, updating dependencies, and tightening infrastructure controls.",
    extra: "Configuration & updates",
    icon: "⚙️",
  },
];

const benefits = [
  {
    title: "Industry-Focused Expertise",
    desc: "We understand the operational and compliance pressures faced by logistics and business-critical platforms.",
  },
  {
    title: "Security That Supports Growth",
    desc: "Our solutions are built to scale with your users, infrastructure, and transaction volumes.",
  },
  {
    title: "Practical Delivery",
    desc: "We prioritize clear fixes, measurable improvements, and operational usability over security theater.",
  },
  {
    title: "Long-Term Resilience",
    desc: "We help you create systems that remain secure, stable, and auditable over time.",
  },
];

const processSteps = [
  {
    step: "01",
    title: "Assess",
    desc: "We review applications, infrastructure, workflows, and risks to identify where your biggest exposures exist.",
  },
  {
    step: "02",
    title: "Design",
    desc: "We recommend realistic security controls, architecture improvements, and process changes tailored to your operations.",
  },
  {
    step: "03",
    title: "Implement",
    desc: "We help put protections in place across authentication, monitoring, data handling, and system configuration.",
  },
  {
    step: "04",
    title: "Monitor",
    desc: "We support ongoing visibility, auditing, and improvements so your defenses stay effective as the business evolves.",
  },
];

const stats = [
  { value: "99.9%", label: "Platform Availability" },
  { value: "24/7", label: "Threat Visibility" },
  { value: "100+", label: "Security Controls Reviewed" },
  { value: "500+", label: "Operational Risks Reduced" },
];

const testimonials = [
  {
    name: "Operations Manager",
    role: "Freight & Distribution",
    text: "The team helped us improve both security and operational confidence. We now have better visibility, stronger access control, and fewer manual risks.",
  },
  {
    name: "Head of IT",
    role: "Business Applications",
    text: "Their approach was clear and practical. Instead of generic recommendations, we received security improvements that fit our systems and workflows.",
  },
  {
    name: "Logistics Director",
    role: "Supply Chain Operations",
    text: "We strengthened document handling, user permissions, and audit traceability without slowing down the business.",
  },
];

const faqs = [
  {
    q: "What kinds of businesses do you support?",
    a: "We support organizations that depend on secure applications, protected data, and reliable operational systems, especially in logistics and business process environments.",
  },
  {
    q: "Do you only provide consulting?",
    a: "No. We support assessments, implementation guidance, system improvement, and operational security enablement.",
  },
  {
    q: "Can you help secure internal platforms?",
    a: "Yes. We help secure internal tools, business applications, user access workflows, and sensitive document processes.",
  },
  {
    q: "Do you support ongoing improvement?",
    a: "Yes. Security is not a one-time task, so we focus on continuous monitoring, review, and optimization.",
  },
];

const Home = () => {
  return (
    <>
      <section className="container-fluid tw-bg-gradient-to-r tw-from-gray-950 tw-via-gray-900 tw-to-gray-800 tw-text-white tw-py-5">
        <div className="container">
          <div className="row align-items-center tw-min-h-[80vh]">
            <motion.div
              className="col-lg-6 mb-5 mb-lg-0"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.span
                variants={fadeUp}
                className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.2em] tw-text-blue-300 tw-mb-3"
              >
                Secure Systems • Protected Data • Reliable Operations
              </motion.span>

              <motion.h1
                variants={fadeUp}
                className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-3"
              >
                Protect Systems. Secure Data. Enable Safe Operations.
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="tw-text-gray-300 tw-text-lg tw-mb-4"
              >
                Practical cybersecurity solutions designed to strengthen
                applications, infrastructure, and logistics platforms without
                slowing down the business.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="tw-flex tw-flex-wrap tw-gap-3"
              >
                <NavLink to="/contact" className="btn btn-primary">
                  Request Demo
                </NavLink>
                <NavLink to="/services" className="btn btn-outline-light">
                  Explore Services
                </NavLink>
              </motion.div>

              <motion.div
                variants={fadeUp}
                className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3 tw-mt-5"
              >
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Secure Access</h6>
                  <p className="tw-text-sm tw-text-gray-300 tw-mb-0">
                    Role-based control across users and workflows.
                  </p>
                </div>
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Data Protection</h6>
                  <p className="tw-text-sm tw-text-gray-300 tw-mb-0">
                    Safer storage, transfer, and handling of sensitive data.
                  </p>
                </div>
                <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-2xl tw-p-3">
                  <h6 className="tw-font-semibold tw-mb-1">Audit Readiness</h6>
                  <p className="tw-text-sm tw-text-gray-300 tw-mb-0">
                    Better traceability, logging, and operational confidence.
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            >
              <div className="tw-relative">
                <img
                  src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b"
                  alt="Cybersecurity"
                  className="img-fluid tw-rounded-3xl tw-shadow-lg tw-w-full"
                />

                <div className="tw-absolute tw-bottom-4 tw-left-4 tw-bg-white tw-text-gray-900 tw-rounded-2xl tw-shadow-xl tw-p-4 tw-max-w-xs">
                  <h6 className="tw-font-bold tw-mb-1">Operational Security</h6>
                  <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
                    Secure workflows, better visibility, and stronger controls
                    across modern business systems.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="container tw-py-5">
        <motion.div
          className="row text-center"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {stats.map((item, i) => (
            <motion.div key={i} className="col-6 col-md-3 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-rounded-2xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                <h2 className="tw-text-3xl tw-font-bold tw-mb-1">{item.value}</h2>
                <p className="tw-text-gray-600 tw-mb-0">{item.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">
            Focused on Real-World Security Problems
          </h2>
          <p className="tw-text-gray-600 tw-max-w-3xl tw-mx-auto">
            We identify meaningful risks and deliver actionable improvements that
            strengthen application security, infrastructure resilience, and data
            protection across operational platforms.
          </p>
        </div>

        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {services.map((item, i) => (
            <motion.div key={i} className="col-md-6 col-lg-3 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-h-full tw-border tw-border-gray-100 hover:tw-shadow-lg hover:tw-border-gray-300 tw-transition-all">
                <div className="tw-text-4xl tw-mb-3">{item.icon}</div>
                <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                <p className="tw-text-gray-600 tw-text-sm tw-mb-3">{item.desc}</p>
                <span className="tw-inline-block tw-text-xs tw-font-medium tw-text-blue-600 tw-bg-blue-50 tw-px-3 tw-py-2 tw-rounded-full">
                  {item.extra}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="container-fluid tw-bg-gray-50 tw-py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="tw-text-3xl tw-font-semibold">Why Choose Us</h2>
            <p className="tw-text-gray-600 tw-max-w-3xl tw-mx-auto">
              We combine cybersecurity discipline with operational understanding
              so protection supports the business instead of getting in its way.
            </p>
          </div>

          <motion.div
            className="row"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {benefits.map((item, i) => (
              <motion.div key={i} className="col-md-6 col-lg-3 mb-4" variants={fadeUp}>
                <div className="tw-bg-white tw-rounded-3xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-100 tw-h-full">
                  <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                  <p className="tw-text-gray-600 tw-text-sm tw-mb-0">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="container-fluid tw-bg-gray-100 tw-py-5">
        <div className="container">
          <div className="row align-items-center">
            <motion.div
              className="col-lg-6 mb-4 mb-lg-0"
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1555949963-aa79dcee981c"
                alt="Security services"
                className="img-fluid tw-rounded-3xl tw-shadow tw-w-full"
              />
            </motion.div>

            <motion.div
              className="col-lg-6"
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <h2 className="tw-text-2xl tw-font-semibold mb-3">
                Security Services for Business Applications
              </h2>

              <p className="tw-text-gray-600 tw-mb-4">
                From secure API design to stronger authentication and audit
                visibility, we help organizations reduce risk across the systems
                that matter most.
              </p>

              <ul className="tw-text-gray-700 tw-space-y-2 tw-pl-4">
                <li>✔ Vulnerability assessment and risk review</li>
                <li>✔ Secure API implementation and validation</li>
                <li>✔ Authentication, authorization, and access control</li>
                <li>✔ Data encryption and secure document workflows</li>
                <li>✔ Logging, monitoring, and traceability improvements</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">
            How We Secure Your Systems
          </h2>
          <p className="tw-text-gray-600 tw-max-w-3xl tw-mx-auto">
            Our process is built to move from discovery to implementation with
            clarity, speed, and measurable security outcomes.
          </p>
        </div>

        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {processSteps.map((item, i) => (
            <motion.div key={i} className="col-md-6 col-lg-3 mb-4" variants={fadeUp}>
              <div className="tw-bg-gray-50 tw-rounded-3xl tw-p-4 tw-h-full tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-text-blue-600 tw-font-bold tw-text-2xl tw-mb-2">
                  {item.step}
                </div>
                <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                <p className="tw-text-gray-600 tw-text-sm tw-mb-0">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="container tw-py-5">
        <div className="row align-items-center tw-min-h-[500px]">
          <motion.div
            className="col-lg-6 tw-flex tw-flex-col tw-justify-center mb-4 mb-lg-0"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <span className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.2em] tw-text-blue-600 tw-mb-2">
              Platform Overview
            </span>

            <h2 className="tw-text-2xl tw-font-semibold mb-3">
              Freight Forwarding Platform with Built-in Security
            </h2>

            <p className="tw-text-gray-600">
              Our logistics platform supports secure operations, controlled
              access, and safer data handling across shipment workflows,
              document processes, and user actions.
            </p>

            <ul className="tw-text-gray-700 tw-mt-3 tw-space-y-2 tw-pl-4">
              <li>• Shipment lifecycle tracking</li>
              <li>• Role-based user permissions</li>
              <li>• Secure document management</li>
              <li>• Audit logs for visibility and traceability</li>
              <li>• Safer collaboration across operational workflows</li>
            </ul>

            <div className="tw-mt-4 tw-flex tw-gap-3 tw-flex-wrap">
              <NavLink to="/products" className="btn btn-primary">
                Explore Product
              </NavLink>
              <NavLink to="/contact" className="btn btn-outline-secondary">
                Book Consultation
              </NavLink>
            </div>
          </motion.div>

          <motion.div
            className="col-lg-6 tw-flex"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <img
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d"
              alt="Freight software"
              className="img-fluid tw-rounded-3xl tw-shadow tw-w-full tw-h-full tw-object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section className="container-fluid tw-bg-gray-100 tw-py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="tw-text-3xl tw-font-semibold">What Clients Say</h2>
            <p className="tw-text-gray-600">
              Trusted by teams that need security improvements without
              operational disruption.
            </p>
          </div>

          <motion.div
            className="row"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {testimonials.map((item, i) => (
              <motion.div key={i} className="col-md-4 mb-4" variants={fadeUp}>
                <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-gray-100 tw-h-full">
                  <p className="tw-text-gray-600 tw-mb-4">“{item.text}”</p>
                  <h6 className="tw-font-semibold tw-mb-1">{item.name}</h6>
                  <p className="tw-text-sm tw-text-gray-500 tw-mb-0">{item.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="container tw-py-5">
        <div className="text-center mb-5">
          <h2 className="tw-text-3xl tw-font-semibold">
            Frequently Asked Questions
          </h2>
          <p className="tw-text-gray-600">
            Answers to common questions about our approach and services.
          </p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="accordion" id="faqAccordion">
              {faqs.map((item, i) => (
                <div
                  className="accordion-item tw-rounded-2xl tw-overflow-hidden tw-mb-3"
                  key={i}
                >
                  <h2 className="accordion-header" id={`heading-${i}`}>
                    <button
                      className={`accordion-button ${i !== 0 ? "collapsed" : ""}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse-${i}`}
                      aria-expanded={i === 0 ? "true" : "false"}
                      aria-controls={`collapse-${i}`}
                    >
                      {item.q}
                    </button>
                  </h2>
                  <div
                    id={`collapse-${i}`}
                    className={`accordion-collapse collapse ${i === 0 ? "show" : ""}`}
                    aria-labelledby={`heading-${i}`}
                    data-bs-parent="#faqAccordion"
                  >
                    <div className="accordion-body">{item.a}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-fluid tw-bg-gray-900 tw-text-white tw-py-5 text-center">
        <div className="container">
          <h2 className="tw-text-3xl tw-font-semibold">
            Strengthen Your System Security Today
          </h2>
          <p className="tw-text-gray-300 tw-max-w-2xl tw-mx-auto">
            Identify vulnerabilities, improve resilience, and build more secure
            operational platforms with practical support that fits your business.
          </p>

          <div className="tw-mt-4 tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
            <NavLink to="/contact" className="btn btn-primary">
              Request Demo
            </NavLink>
            <NavLink to="/services" className="btn btn-outline-light">
              Learn More
            </NavLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;